import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ListItem from "./ListItem";

const SectionContainer = styled.div`
  display: flex;
  align-items: flex-start;
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 0 0 20px 0;
  page-break-inside: avoid;
  break-inside: avoid;

  @media screen and (max-width: 991px) {
    flex-direction: column;
  }

  @media print {
    margin-bottom: 5px;
  }
`;

const DateContainer = styled.div`
  width: 170px;
  text-align: right;
  font-style: italic;
  font-size: 0.9rem;
  padding-right: 20px;
  padding-top: 10px;

  @media screen and (max-width: 991px) {
    text-align: left;
    padding-top: 15px;
    padding-bottom: 5px;
    padding-left: 80px;
  }

  @media print {
    padding-top: 8px;
  }
`;

const ContentContainer = styled.div`
  width: 75%;
  display: flex;
  flex-direction: column;
  padding-left: 60px;

  @media screen and (max-width: 991px) {
    padding-left: 80px;
  }

  @media print {
    margin-right: 60px;
    padding-left: 30px;
  }
`;

const Circle = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "important",
})`
  width: ${(props) => (props.important ? "15px" : "12px")};
  height: ${(props) => (props.important ? "15px" : "12px")};
  border-radius: 50%;
  border: 1.7px solid #000;
  background-color: ${(props) => (props.important ? "#538135" : "#FFF")};
  position: absolute;
  left: ${(props) => (props.important ? "192px" : "194px")};
  top: ${(props) => (props.important ? "12px" : "13px")};

  @media screen and (max-width: 991px) {
    left: ${(props) => (props.important ? "29px" : "30px")};
    top: ${(props) => (props.important ? "17px" : "19px")};
  }

  @media print {
    transform: scale(0.66);
    left: ${(props) => (props.important ? "167px" : "168px")};
    top: ${(props) => (props.important ? "6px" : "8px")};
  }
`;

const PositionText = styled.h3`
  font-size: 1.3rem;
  font-variant: small-caps;
  font-weight: normal;
  color: #538135;
  margin-top: 5px;
  margin-bottom: 5px;
`;

const TitleText = styled.p`
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 10px;
  margin-top: 0px;
`;

const DescriptionText = styled.p`
  font-size: 1rem;
  font-style: italic;
  margin-top: 0px;
  margin-bottom: 0px;
`;

const ListContainer = styled.div`
  margin-top: 16px;
  font-size: 0.95rem;
  display: ${(props) => (props.exportVersion ? "none" : "flex")};
  flex-direction: column;
  gap: 8px;

  @media print {
    display: none;
  }
`;

const BulletList = styled.ul`
  margin-top: 16px;
  padding-left: 20px;
  font-size: 0.95rem;
  display: ${(props) => (props.exportVersion ? "block" : "none")};

  li {
    margin-bottom: 4px;
  }

  @media print {
    display: block;
  }
`;

const Section = ({ title, date, position, items, description, important }) => {
  const [exportVersion, setExportVersion] = useState(true);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const exportParam = params.get("exportVersion") === "true";
      setExportVersion(exportParam);
    }
  }, []);

  return (
    <SectionContainer>
      <DateContainer>{date}</DateContainer>
      <Circle important={important} />
      <ContentContainer>
        {position && <PositionText>{position}</PositionText>}
        {title && <TitleText>{title}</TitleText>}
        {description && <DescriptionText>{description}</DescriptionText>}
        {items && items.length > 0 && (
          <>
            <ListContainer exportVersion={exportVersion}>
              {items.map((item, index) => (
                <ListItem
                  key={index}
                  title={item.title}
                  text={item.longDescription}
                  thumbnail={item.thumbnail}
                  technologies={item.technologies}
                  link={item.link}
                />
              ))}
            </ListContainer>

            <BulletList exportVersion={exportVersion}>
              {items.map((item, index) => (
                <li key={index}>{item.shortDescription}</li>
              ))}
            </BulletList>
          </>
        )}
      </ContentContainer>
    </SectionContainer>
  );
};

export default Section;
